import React, { useEffect, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import { useRealAccounts } from "../hooks/useRealAccounts";
import { useAccountBalance } from "../hooks/useAccountBalance";
import { useUser } from "../context/UserContext";

// Set to true whenever MT5 Account withdrawal needs to be re-enabled
const ENABLE_MT5_WITHDRAW = false;

export default function CryptoWithdraw() {
  const { userData, fetchUserData } = useUser();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const { accounts } = useRealAccounts();

  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);

  const [formData, setFormData] = useState({
    withdraw_type: "COMMISSION",
    account_number: "",
    password: "",
    address: "",
    amount: ""
  });

  const [showPassword, setShowPassword] = useState(false);
  const { balance, loadingBalance, fetchBalance } = useAccountBalance(formData.withdraw_type === 'MT5' ? formData.account_number : "");
  const [formErrors, setFormErrors] = useState({});

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [timer, setTimer] = useState(120);
  const [, setCanResend] = useState(false);
  const [, setPaymentId] = useState("");
  const [, setWithdrawalId] = useState(0);
  const [, setWithdrawResponseData] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalPassword, setModalPassword] = useState("");
  const [modalPasswordError, setModalPasswordError] = useState("");

  useEffect(() => {
    let interval;
    if (showOtpModal && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, timer]);

  useEffect(() => {
    fetchChains();
  }, []);

  useEffect(() => {
    if (formData.withdraw_type === 'MT5' && accounts.length > 0 && !formData.account_number) {
      setFormData(prev => ({ ...prev, account_number: String(accounts[0]) }));
    }
  }, [formData.withdraw_type, accounts, formData.account_number]);

  useEffect(() => {
    if (otp.length === 6 && showOtpModal && !otpLoading) {
      const timer = setTimeout(() => {
        handleConfirmWithdraw();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, showOtpModal]);

  const fetchChains = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/withdraw/chains`, {
        headers: getApiHeaders()
      });
      const resData = response.data;
      let chainList = [];

      if (Array.isArray(resData)) {
        chainList = resData;
      } else if (resData?.data && Array.isArray(resData.data)) {
        chainList = resData.data;
      } else if (resData?.chains && Array.isArray(resData.chains)) {
        chainList = resData.chains;
      }

      // Filter active networks if specified
      const activeChains = chainList.filter((c) => c.is_active !== false);

      setChains(activeChains);
      if (activeChains.length > 0) {
        setSelectedChain(activeChains[0]);
      } else {
        setSelectedChain(null);
      }
    } catch (err) {
      console.error("Error fetching chains:", err);
      handleError(err);
      setChains([]);
      setSelectedChain(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === 'number' && Number(value) < 0) {
      value = Math.abs(value).toString();
    }
    if (name === "amount") {
      value = String(value).replace(/[^0-9.]/g, "");
      if (value.includes("-") || value.toLowerCase().includes("e")) {
        return;
      }
    }
    if (name === "account_number") {
      value = value.replace(/\D/g, "");
    }
    if (name === "withdraw_type") {
      setFormData(prev => ({
        ...prev,
        withdraw_type: value,
        account_number: value === 'MT5' ? (accounts.length > 0 ? String(accounts[0]) : "") : ""
      }));
      setFormErrors(prev => ({ ...prev, [name]: "", account_number: "" }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateCryptoAddress = (address, chain) => {
    if (!address) return false;
    const typeStr = String(chain?.type || "").toUpperCase();
    const networkStr = String(chain?.network || "").toUpperCase();

    // TRC20 / Tron: starts with 'T' and 34 characters
    if (typeStr.includes("TRC20") || networkStr.includes("TRON")) {
      const trc20Regex = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
      return trc20Regex.test(address);
    }

    // BEP20 / ERC20 / Binance / Ethereum: starts with '0x' and 42 characters
    if (
      typeStr.includes("BEP20") ||
      typeStr.includes("ERC20") ||
      networkStr.includes("BINANCE") ||
      networkStr.includes("ETHEREUM") ||
      networkStr.includes("BSC")
    ) {
      const ethRegex = /^0x[a-fA-F0-9]{40}$/;
      return ethRegex.test(address);
    }

    return address.length > 10;
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setModalPassword("");
    setModalPasswordError("");
  };

  const executeBeforeWithdraw = async () => {
    if (!selectedChain) {
      MessageToster("error", "Please select a network.");
      return;
    }

    setLoading(true);
    const amountNum = parseFloat(formData.amount);
    const addressStr = formData.address?.trim();
    const payload = {
      chain_type: selectedChain.network,
      withdrawal_amount: amountNum,
      token_symbol: "USDT",
      withdrawal_address: addressStr
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/before_withdraw`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "OTP sent successfully.");
        setTimer(120);
        setCanResend(false);
        setOtp("");
        setPaymentId(response.data?.response?.paymentid || "");
        setWithdrawalId(response.data?.id || 0);
        setWithdrawResponseData(response.data);
        setShowPasswordModal(false);
        setModalPassword("");
        setShowOtpModal(true);
      } else {
        MessageToster("error", response.data?.message || "Validation failed.");
      }
    } catch (error) {
      console.error("Before withdraw error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChain) {
      MessageToster("error", "Please select a network.");
      return;
    }

    const newErrors = {};
    const addressStr = formData.address?.trim();

    if (formData.withdraw_type === 'MT5' && !formData.account_number) {
      newErrors.account_number = "MT5 account not available.";
    }

    if (!addressStr) {
      newErrors.address = "Please enter a withdrawal address.";
    } else if (!validateCryptoAddress(addressStr, selectedChain)) {
      newErrors.address = `Please enter a valid ${selectedChain.type || 'network'} address.`;
    }

    const amountNum = parseFloat(formData.amount);
    const effectiveMinWithdraw = 10;

    if (!formData.amount) {
      newErrors.amount = "Please enter an amount.";
    } else if (isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = "Please enter a valid positive amount.";
    } else if (amountNum < effectiveMinWithdraw) {
      newErrors.amount = `Minimum withdrawal amount is $${effectiveMinWithdraw}.`;
    }

    // Check available balance
    const availableBalance = formData.withdraw_type === 'COMMISSION'
      ? (userData?.commissionwallet !== undefined ? Number(userData.commissionwallet) : 0)
      : (balance !== null ? Number(balance) : 0);

    if (amountNum > availableBalance) {
      newErrors.amount = `Insufficient balance. Available: $${availableBalance.toFixed(2)}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    setFormErrors({});

    if (formData.withdraw_type === 'MT5') {
      setShowPasswordModal(true);
    } else {
      executeBeforeWithdraw();
    }
  };

  const handleResendOtp = async () => {
    setOtp("");
    setTimer(120);
    setCanResend(false);

    const amountNum = parseFloat(formData.amount);
    const payload = {
      chain_type: selectedChain?.network,
      withdrawal_amount: amountNum,
      token_symbol: "USDT",
      withdrawal_address: formData.address?.trim()
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/before_withdraw`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "OTP resent successfully.");
        setPaymentId(response.data?.response?.paymentid || "");
        setWithdrawalId(response.data?.id || 0);
        setWithdrawResponseData(response.data);
      } else {
        MessageToster("error", response.data?.message || "Failed to resend OTP.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    }
  };

  const handleConfirmWithdraw = async () => {
    if (!otp) {
      MessageToster("error", "Please enter the OTP.");
      return;
    }

    setOtpLoading(true);
    const payload = {
      chain_type: selectedChain?.network,
      withdrawal_amount: parseFloat(formData.amount),
      token_symbol: "USDT",
      withdrawal_address: formData.address?.trim(),
      otp: parseInt(otp, 10)
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/withdraw`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "Withdrawal successful!");
        setShowOtpModal(false);
        if (fetchUserData) fetchUserData();
        if (fetchBalance) fetchBalance();
        navigate("/withdraw-history");
      } else {
        MessageToster("error", response.data?.message || "Withdrawal failed.");
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const calculatedProfitAmount = balance !== null ? Math.max(0, Number(balance) - (userData?.Deposit_Amount !== undefined ? Number(userData.Deposit_Amount) : 0)).toFixed(2) : "0.00";

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-0">Crypto Withdraw</h2>
                    <div className="page-title-right">
                      <button
                        className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                        onClick={() => navigate("/withdraw-history")}
                      >
                        <i className="bx bx-history me-2 fs-5"></i> Withdraw History
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="card shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <div className="card-body p-4">
                      {loading && chains.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                          <p className="mt-2 text-muted">Loading available networks...</p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} noValidate>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label text-muted fw-semibold">Select Network</label>
                              <select
                                className="form-select px-3 py-2 fw-medium shadow-none"
                                value={selectedChain ? String(selectedChain.id) : ""}
                                onChange={(e) => {
                                  const found = chains.find((c) => String(c.id) === String(e.target.value));
                                  if (found) setSelectedChain(found);
                                }}
                                disabled={chains.length === 0}
                                style={{ cursor: chains.length > 0 ? "pointer" : "not-allowed", borderRadius: "8px" }}
                              >
                                {chains.length === 0 ? (
                                  <option value="">No networks available</option>
                                ) : (
                                  chains.map((chain) => (
                                    <option key={chain.id} value={chain.id}>
                                      {chain.type}
                                    </option>
                                  ))
                                )}
                              </select>
                            </div>

                            <div className="col-md-6 mb-3">
                              <div className="d-flex justify-content-between">
                                <label className="form-label text-muted fw-semibold">Account Type</label>
                              </div>
                              <select
                                className="form-select px-3 py-2 fw-medium shadow-none"
                                name="withdraw_type"
                                value={formData.withdraw_type}
                                onChange={handleInputChange}
                                style={{ cursor: 'pointer', borderRadius: "8px" }}
                              >
                                <option value="COMMISSION">INCOME WALLET</option>
                                {/* Set ENABLE_MT5_WITHDRAW = true above to quickly re-enable MT5 Account withdrawal */}
                                {ENABLE_MT5_WITHDRAW && (
                                  <option value="MT5">
                                    MT5 Account {accounts.length > 0 ? `(${accounts[0]})` : ""}
                                  </option>
                                )}
                              </select>
                            </div>
                          </div>

                          {selectedChain ? (
                            <>
                              <div className="row">
                                <div className="col-md-6 mb-3">
                                  <label className="form-label text-muted fw-semibold">Available Balance</label>
                                  <div className="form-control px-3 py-2 fw-medium" style={{ cursor: 'not-allowed', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: "8px" }}>
                                    {formData.withdraw_type === 'COMMISSION' ? (
                                      <span>{`$${userData?.commissionwallet !== undefined ? userData.commissionwallet : '0.00'}`}</span>
                                    ) : (
                                      <span>{loadingBalance ? 'Loading...' : `$${balance !== null ? balance : '0.00'}`}</span>
                                    )}
                                  </div>
                                  {formErrors.account_number && (
                                    <small className="text-danger mt-1 d-block">{formErrors.account_number}</small>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label text-muted fw-semibold">Amount</label>
                                  <div className="input-group">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className={`form-control px-3 py-2 ${formErrors.amount ? 'is-invalid border-danger' : ''}`}
                                      placeholder={`Min Withdraw: $10`}
                                      name="amount"
                                      value={formData.amount}
                                      onKeyDown={(e) => {
                                        if (["e", "E", "+", "-"].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onChange={handleInputChange}
                                      style={{ borderRadius: "8px 0 0 8px" }}
                                      required
                                    />
                                    <span className="input-group-text fw-bold notranslate" translate="no" style={{ borderRadius: "0 8px 8px 0" }}>USDT</span>
                                  </div>
                                  {formErrors.amount && (
                                    <small className="text-danger mt-1 d-block">{formErrors.amount}</small>
                                  )}
                                  <small className="text-muted mt-1 d-block">
                                    Min Withdraw: $10
                                  </small>
                                </div>
                              </div>

                              {ENABLE_MT5_WITHDRAW && formData.withdraw_type === 'MT5' && (
                                <div className="row mb-3">
                                  <div className="col-md-6">
                                    <div className="card mb-3 mb-md-0 shadow-none border" style={{ borderRadius: '12px' }}>
                                      <div className="card-body p-3 d-flex align-items-center">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-primary-subtle" style={{ width: '48px', height: '48px' }}>
                                          <i className="bx bx-down-arrow-circle fs-3 text-primary"></i>
                                        </div>
                                        <div>
                                          <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deposit Amount</p>
                                          <h5 className="mb-0 fw-bold">${userData?.Deposit_Amount !== undefined ? Number(userData.Deposit_Amount).toFixed(2) : "0.00"}</h5>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="card shadow-none border" style={{ borderRadius: '12px' }}>
                                      <div className="card-body p-3 d-flex align-items-center">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-primary-subtle" style={{ width: '48px', height: '48px' }}>
                                          <i className="bx bx-user-circle fs-3 text-primary"></i>
                                        </div>
                                        <div>
                                          <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Profit Amount</p>
                                          <h5 className="mb-0 fw-bold">${calculatedProfitAmount}</h5>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {(() => {
                                const effectiveMinWithdraw = 10;
                                const amountNum = parseFloat(formData.amount) || 0;
                                const rawFee = selectedChain
                                  ? (selectedChain.withdraw_fee !== undefined && selectedChain.withdraw_fee !== null
                                    ? selectedChain.withdraw_fee
                                    : (selectedChain.fees !== undefined && selectedChain.fees !== null
                                      ? selectedChain.fees
                                      : (userData?.withdraw_fee_per !== undefined && userData?.withdraw_fee_per !== null
                                        ? userData.withdraw_fee_per
                                        : 0)))
                                  : 0;
                                const feePercent = isNaN(parseFloat(rawFee)) ? 0 : parseFloat(rawFee);
                                const chargeAmount = (amountNum * feePercent) / 100;
                                const receivableAmount = Math.max(0, amountNum - chargeAmount);

                                return (
                                  <>
                                    <div className="row">
                                      <div className="col-12 mb-3">
                                        <label className="form-label text-muted fw-semibold">Withdrawal Address</label>
                                        <input
                                          type="text"
                                          className={`form-control px-3 py-2 ${formErrors.address ? 'is-invalid border-danger' : ''}`}
                                          placeholder={`Enter ${selectedChain.type} address`}
                                          name="address"
                                          value={formData.address}
                                          onChange={handleInputChange}
                                          style={{ borderRadius: "8px" }}
                                          required
                                        />
                                        {formErrors.address ? (
                                          <small className="text-danger mt-1 d-block">{formErrors.address}</small>
                                        ) : (
                                          <small className="text-muted mt-1 d-block">
                                            <i className="bx bx-info-circle me-1"></i>
                                            Please ensure you enter a valid {selectedChain.type} address. Assets sent to the wrong network cannot be recovered.
                                          </small>
                                        )}
                                      </div>
                                    </div>

                                    <div className="row mb-4">
                                      <div className="col-md-6">
                                        <div className="card mb-3 mb-md-0 shadow-none border border-danger-subtle" style={{ borderRadius: '12px' }}>
                                          <div className="card-body p-3 d-flex align-items-center">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-danger-subtle" style={{ width: '48px', height: '48px' }}>
                                              <i className="bx bx-receipt fs-3 text-danger"></i>
                                            </div>
                                            <div>
                                              <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Charge ({feePercent}%)</p>
                                              <h5 className="text-danger mb-0 fw-bold notranslate" translate="no">
                                                <span>${chargeAmount.toFixed(2)}</span>
                                              </h5>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="card shadow-none border border-success-subtle" style={{ borderRadius: '12px' }}>
                                          <div className="card-body p-3 d-flex align-items-center">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-success-subtle" style={{ width: '48px', height: '48px' }}>
                                              <i className="bx bx-wallet-alt fs-3 text-success"></i>
                                            </div>
                                            <div>
                                              <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Receivable Amount</p>
                                              <h5 className="text-success mb-0 fw-bold notranslate" translate="no">
                                                <span>${receivableAmount.toFixed(2)}</span>
                                              </h5>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="text-end mt-4">
                                      <button type="submit" className="btn btn-primary px-4 py-2 fw-medium" disabled={loading}>
                                        {loading ? (
                                          <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                                        ) : (
                                          <span>Submit Withdrawal</span>
                                        )}
                                      </button>
                                    </div>
                                  </>
                                );
                              })()}
                            </>
                          ) : (
                            !loading && (
                              <div className="alert alert-warning d-flex align-items-center mt-3" role="alert">
                                <i className="bx bx-info-circle fs-4 me-2"></i>
                                <div>No withdrawal networks available currently. Please contact support.</div>
                              </div>
                            )
                          )}
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Password Modal Overlay (kept for MT5 if re-enabled) */}
      {showPasswordModal && (
        <>
          <div className="modal-backdrop fade show" style={{ backdropFilter: 'blur(4px)' }}></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title fw-bold fs-4">Security Verification</h5>
                  <button type="button" className="btn-close" onClick={handleClosePasswordModal} disabled={loading}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-4 text-primary bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bx bx-lock-alt" style={{ fontSize: '48px' }}></i>
                  </div>
                  <p className="text-muted mb-4 px-3">
                    Please enter your MT5 Master Password to confirm this withdrawal.
                  </p>

                  <div className="mb-4 text-start">
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`form-control px-3 py-2 ${modalPasswordError ? 'is-invalid border-danger' : ''}`}
                        placeholder="Enter MT5 Master Password"
                        value={modalPassword}
                        onChange={(e) => {
                          setModalPassword(e.target.value);
                          if (e.target.value) setModalPasswordError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (!modalPassword) setModalPasswordError("Please enter your MT5 Master Password.");
                            else executeBeforeWithdraw();
                          }
                        }}
                        disabled={loading}
                      />
                      <button
                        className="btn btn-outline-secondary border"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={showPassword ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"} />
                      </button>
                    </div>
                    {modalPasswordError && (
                      <small className="text-danger mt-1 d-block">{modalPasswordError}</small>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary px-4 py-2" onClick={handleClosePasswordModal} disabled={loading}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4 py-2 fw-medium"
                      disabled={loading}
                      onClick={() => {
                        if (!modalPassword) {
                          setModalPasswordError("Please enter your MT5 Master Password.");
                        } else {
                          executeBeforeWithdraw();
                        }
                      }}
                    >
                      {loading ? (
                        <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                      ) : (
                        <span>Submit</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* OTP Modal Overlay */}
      {showOtpModal && (
        <>
          <div className="modal-backdrop fade show" style={{ backdropFilter: 'blur(4px)' }}></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title fw-bold fs-4">Security Verification</h5>
                  <button type="button" className="btn-close" onClick={() => setShowOtpModal(false)} disabled={otpLoading}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-4 text-primary bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bx bx-shield-quarter" style={{ fontSize: '48px' }}></i>
                  </div>
                  <p className="text-muted mb-4 px-3">
                    Please enter the 6-digit verification code sent to your registered email or phone to securely confirm this withdrawal.
                  </p>

                  <div className="d-flex justify-content-center gap-2 mb-4">
                    {[0, 1, 2, 3, 4, 5].map((index) => {
                      return (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="form-control text-center fw-bold text-primary shadow-sm"
                          style={{
                            width: "45px",
                            height: "55px",
                            fontSize: "24px",
                            borderRadius: "12px",
                            border: "2px solid #e0e0e0",
                            transition: "all 0.2s"
                          }}
                          value={otp[index] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!/^[0-9]*$/.test(val)) return;

                            let newOtpArray = otp.split("");
                            newOtpArray[index] = val.substring(val.length - 1);
                            setOtp(newOtpArray.join(""));

                            if (val && e.target.nextElementSibling) {
                              e.target.nextElementSibling.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Backspace") {
                              if (!otp[index] && e.target.previousElementSibling) {
                                e.target.previousElementSibling.focus();
                              } else if (otp[index]) {
                                let newOtpArray = otp.split("");
                                newOtpArray[index] = "";
                                setOtp(newOtpArray.join(""));
                              }
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedData = e.clipboardData.getData("text/plain").trim();
                            if (!/^[0-9]+$/.test(pastedData)) return;

                            const pastedChars = pastedData.substring(0, 6).split("");
                            let newOtpArray = Array(6).fill("");

                            for (let i = 0; i < pastedChars.length; i++) {
                              newOtpArray[i] = pastedChars[i];
                            }
                            setOtp(newOtpArray.join(""));

                            const inputs = e.target.parentElement.querySelectorAll('input');
                            const targetIndex = Math.min(5, pastedChars.length);
                            if (inputs[targetIndex] && pastedChars.length < 6) {
                              inputs[targetIndex].focus();
                            } else if (inputs[5]) {
                              inputs[5].focus();
                            }
                          }}
                        />
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-100 fw-bold rounded-3 shadow-sm mt-3"
                    disabled={otpLoading || otp.length !== 6}
                    onClick={handleConfirmWithdraw}
                  >
                    {otpLoading ? (
                      <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                    ) : (
                      <span>Confirm Withdrawal</span>
                    )}
                  </button>

                  <div className="mt-3">
                    {timer > 0 ? (
                      <p className="text-muted mb-0">Resend OTP in <span className="fw-bold text-primary">{timer}s</span></p>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none fw-bold p-0 m-0"
                        onClick={handleResendOtp}
                        disabled={otpLoading}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
